<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import sectionSorter from '@@/bits/section_sorter'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { defineAsyncComponent, vDel } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id } from '@@/types'
import SurfacePostsDraggablePad from '@@/vuecomponents/SurfacePostsDraggablePad.vue'
import SurfacePostsGridSection from '@@/vuecomponents/SurfacePostsGridSection.vue'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { useSurfaceSectionsDragSortable } from '@@/vuecomposables/surface_sections_drag_sortable'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance, Ref } from 'vue'
import { computed, ref } from 'vue'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfacePostsStore = useSurfacePostsStore()
const surfaceSectionsStore = useSurfaceSectionsStore()

const { backgroundLuminance, dir, isReadOnly, isScreenshotMode, isFrozen } = storeToRefs(useSurfaceStore())
const { isASectionDragging } = storeToRefs(surfaceSectionsStore)
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { sortedGroups, isGroupBySection, isGroupBySingleSelect } = storeToRefs(useSurfaceWishArrangementStore())

const { shouldDisplayGroup } = surfacePostsStore

const rootElement: Ref<HTMLDivElement | null> = ref(null)

function assignRootElementRef(v: ComponentPublicInstance | null): void {
  rootElement.value = v?.$el as HTMLDivElement | null
}

if (!isAppUsing('pdndSurfaceGrid')) {
  useSurfaceSectionsDragSortable({
    onDragStart: () => {
      isASectionDragging.value = true
    },
    onDragEnd: ({ groupId }) => {
      isASectionDragging.value = false
      if (groupId) {
        const sectionElement = document.querySelector(`#section-${groupId}`) as HTMLElement | undefined
        if (!sectionElement) return
        const sortResult = sectionSorter.getNewIndex(sectionElement)
        if (!sortResult) return
        surfaceSectionsStore.sortSection(sortResult)
      }
    },
  })
}

useScrollToNewestPost(rootElement)

const postGroupHeaderHeights = ref<Record<Group['id'], number>>({})

const maxPostGroupHeaderHeight = computed(() => Math.max(...Object.values(postGroupHeaderHeights.value)))

function updatePostGroupHeaderHeights(groupId: Group['id'], height: number): void {
  postGroupHeaderHeights.value = {
    ...postGroupHeaderHeights.value,
    [groupId]: height,
  }
}

function removePostGroupHeaderHeight(groupId: Group['id']): void {
  vDel(postGroupHeaderHeights.value, groupId)
}
</script>

<template>
  <SurfacePostsDraggablePad
    :ref="assignRootElementRef"
    :dir="dir"
    :class="[
      'flex',
      'flex-row',
      'space-s-4',
      // Removing overflow classes increase document.documentElement.scrollWidth and allows full screenshot with full_width param
      !isScreenshotMode && 'overflow-y-hidden overflow-x-auto',
      {
        'scrollbar-photo': backgroundLuminance === 'light',
        'scrollbar-photo-dark': backgroundLuminance === 'dark',
      },
      'mx-auto',
      'box-border',
      'w-full',
      'px-3 tablet-portrait:px-4',
      'overflow-y-scroll',
    ]"
  >
    <SurfacePostsGridSection
      v-for="group in sortedGroups"
      v-show="shouldDisplayGroup(group)"
      :key="group.id"
      :group="group"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
      :post-group-header-heights="postGroupHeaderHeights"
      :max-post-group-header-height="maxPostGroupHeaderHeight"
      @update-post-group-header-heights="updatePostGroupHeaderHeights"
      @remove-post-group-header-height="removePostGroupHeaderHeight"
    />

    <div
      v-if="
        (isGroupBySection || isGroupBySingleSelect) &&
        canIModerate &&
        !isReadOnly &&
        !isFrozen &&
        !isDemoPadletPanelDesktop
      "
      key="surface-add-section-button"
      class="flex-none flex flex-col items-start justify-end me-auto w-fit mt-2"
      :style="{ height: `${maxPostGroupHeaderHeight}px`, marginInlineEnd: 'auto' }"
    >
      <SurfaceAddSectionButton class="mb-11 tablet-portrait:mb-12" />
    </div>
  </SurfacePostsDraggablePad>
</template>
